import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Col, Form, Image, Layout, PageHeader, Row, Select, Space } from "antd";
import { useApplicationContext } from "components/Application";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";

const articleItemQuery = gql`
  query($id: ID!, $pagination: ConnectionPagination, $sorting: ArticleItemSorting, $searchQuery: String) {
    articleItem(id: $id) {
      id
      brand {
        id
      }
      suggestedItems {
        id
        code
      }
    }
    articleItems(pagination: $pagination, sorting: $sorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
        nameEn
        cover
        brand {
          id
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const updateItemSuggestionsMutation = gql`
  mutation($id: ID!, $input: UpdateItemSuggestionsInput) {
    updateItemSuggestions(id: $id, input: $input) {
      results {
        successful
      }
    }
  }
`;

interface FieldData {
  name: string;
  errors: string[];
}

export const Edit: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();

  const { error, loading: queryLoading, data } = useQuery(articleItemQuery, {
    variables: { id },
    fetchPolicy: "cache-and-network",
  });

  const [execMutation, { loading }] = useMutation(updateItemSuggestionsMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      const fieldsErrors: FieldData[] = [];
      const otherErrors: string[] = [];
      graphQLErrors?.forEach((graphQLError) => {
        if (graphQLError.name) {
          fieldsErrors.push({ name: graphQLError.name, errors: (graphQLError as any).messages });
        } else {
          otherErrors.push(graphQLError.message);
        }
      });
      form.setFields(fieldsErrors);
      setOtherErrors(otherErrors);
    },
    onCompleted: (data) => {
      navigate("..");
    },
  });

  const onFinish = (values: any) => {
    execMutation({
      variables: {
        id,
        input: values.suggestions.map((suggestedItemId: any) => ({
          itemId: id,
          suggestedItemId,
        })),
      },
    });
  };

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="..">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  const {
    articleItems: { nodes: suggestedItems },
  } = data;

  return (
    <Fragment>
      <PageHeader
        ghost={true}
        title="Edit Suggestions"
        extra={
          <Row gutter={[24, 0]}>
            <Col>
              <NavLink to="..">
                <Button>Cancel</Button>
              </NavLink>
            </Col>
            <Col>
              <Button
                type="primary"
                loading={loading}
                onClick={() => {
                  form.submit();
                }}
              >
                Save
              </Button>
            </Col>
          </Row>
        }
      />

      <Layout.Content className="content">
        {otherErrors.length > 0 && (
          <Space direction="vertical" style={{ marginBottom: "10px" }}>
            {otherErrors.map((otherError, index) => (
              <Alert key={index} type="error" message="Error" description={otherError} />
            ))}
          </Space>
        )}
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <Form.Item name="suggestions">
                <Select
                  mode="multiple"
                  placeholder="Select other items"
                  allowClear
                  style={{ height: "100%" }}
                  defaultValue={data.articleItem.suggestedItems.map((item: any) => item.id)}
                  filterOption={(input: string, option: any) => {
                    return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                  }}
                >
                  {suggestedItems
                    ?.filter((item: any) =>
                      item.id === id || item.brand.id !== data.articleItem.brand.id ? false : true,
                    )
                    .map((item: any) => (
                      <Select.Option key={item.id} value={item.id} label={item.code}>
                        <div className="demo-option-label-item">
                          <Space align="center">
                            <span role="img">
                              <Image src={`${appEndpointStore}${item.cover}`} width={50} />
                            </span>
                            {item.code}
                          </Space>
                        </div>
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Layout.Content>
    </Fragment>
  );
};
